import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, resolveDirective as _resolveDirective, createElementBlock as _createElementBlock, withDirectives as _withDirectives, unref as _unref, vShow as _vShow, withModifiers as _withModifiers, createSlots as _createSlots, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-ff04cd22"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "grid-3 gap-sm" }
const _hoisted_2 = { class: "panel span-2" }
const _hoisted_3 = {
  key: 0,
  class: "panel"
}
const _hoisted_4 = { key: 0 }
const _hoisted_5 = ["src"]
const _hoisted_6 = { class: "panel" }

import { onMounted, ref, watch } from 'vue';
import { useGettext } from '@jshmrtn/vue3-gettext';
import { useStore } from 'vuex';
import { AppStore } from '@/store';
import ApiResource from '@/core/http/ApiResource';
import { Customer, CustomerLawFirm, FullCustomer, useCustomer } from '../Customer';
import { CustomerSubscription, useCustomerSubscription } from '../CustomerSubscription';
import { useCustomerUser } from '@/modules/customer/CustomerUser';
import { LawFirm } from '@/modules/law_firm/LawFirm';
import { UserUpdate } from '@/modules/user/User';
import { ErrorBag, handleFormError, TabId } from '@/core/common';
import AppButton from '@/core/components/form/AppButton.vue';
import PageLayout from '@/core/components/PageLayout.vue';
import AppTabs from '@/core/components/AppTabs.vue';
import AppFormGroup from '@/core/components/form/AppFormGroup.vue';
import AppFileInput from '@/core/components/form/AppFileInput.vue';
import CustomerTicketFormInfo from './CustomerTicketFormInfo.vue';
import CustomerFormBasics from './CustomerFormBasics.vue';
import CustomerFormUser from './CustomerFormUser.vue';
import CustomerFormLanguages from './CustomerFormLanguages.vue';
import CustomerFormInvoiceAddress from './CustomerFormInvoiceAddress.vue';
import CustomerFormFirstSubscription from './CustomerFormFirstSubscription.vue';
import CustomerFormSubscriptionList from './CustomerFormSubscriptionList.vue';

interface CustomerFormProps {
	pageTitle: string;
	customer: FullCustomer;
	forCreate: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CustomerForm',
  props: {
    pageTitle: {},
    customer: {},
    forCreate: { type: Boolean }
  },
  emits: [
	'cancel',
	'saved',
],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { $gettext } = useGettext();
const resource = useCustomer();
const store = useStore<AppStore>();
let subscriptionResource: ApiResource<CustomerSubscription>;
const errors = ref<ErrorBag>({});
const userErrors = ref<ErrorBag>({});
const subscriptionErrors = ref<ErrorBag>({});
const lawFirmErrors = ref<ErrorBag>({});
const languageErrors = ref<ErrorBag>({});
const data = ref<Partial<FullCustomer>>();
const newLogotypeFile = ref<File | null>(null);
const tabs = ref<TabId[]>([
	{ id: 'basics', name: $gettext('Översikt') },
	{ id: 'subscriptions', name: $gettext('Abonnemang/fakturering') },
]);
const currentTab = ref<string>('basics');
const user = ref<Partial<UserUpdate>>({});
const subscriptions = ref<Partial<CustomerSubscription>[]>([]);
const lawFirmsToRemove: LawFirm[] = [];
let storedLanguages: string[] = [];
const languagesToAdd: string[] = [];
const languagesToRemove: string[] = [];

// Set data to form on mount and when the prop value changes.
const setData = async () => {
	if (!props.customer) {
		return;
	}

	data.value = Object.assign({}, props.customer);
	storedLanguages = data.value.languages || [];

	if (props.forCreate) {
		subscriptions.value.push({
			product: {},
		});
	} else {
		subscriptionResource = useCustomerSubscription(props.customer.id);
		subscriptions.value = await subscriptionResource.getAllNoPagination();
	}
};
onMounted(setData);
watch(() => props.customer, setData);

const logotypeSelected = (file: File) => {
	newLogotypeFile.value = file;
};

const lawFirmRemoved = (lawFirm: LawFirm) => {
	lawFirmsToRemove.push(lawFirm);
};

const languageAdded = (languageId: string) => {
	if (!data.value) {
		return;
	}

	if (typeof data.value.languages === 'undefined') {
		data.value.languages = [];
	}

	if (data.value.languages.indexOf(languageId) >= 0) {
		return;
	}

	data.value.languages.push(languageId);
	languagesToAdd.push(languageId);
};

const languageRemoved = (languageId: string) => {
	if (!data.value?.languages) {
		return;
	}

	const index = data.value.languages.indexOf(languageId);

	if (index < 0) {
		return;
	}

	data.value.languages.splice(index, 1);

	languagesToRemove.push(languageId);
};

const save = async () => {
	errors.value = {};

	const lawFirms = data.value?.law_firms || [];
	const languages = data.value?.languages || [];
	const postBody: Record<string, string | number> = Object.assign({}, data.value) as Record<string, string | number>;
	let keepSaving = true;

	delete postBody.whitelabeling;

	if (data.value?.partner) {
		postBody.partner = data.value?.partner.id;
	}

	let promise: Promise<Customer>;
	if (props.customer?.id) {
		promise = resource.put(props.customer.id, postBody);
	} else {
		promise = resource.post(postBody);
	}

	try {
		const whitelabeling = data.value?.whitelabeling;
		data.value = await promise;
		data.value.law_firms = lawFirms;
		data.value.languages = languages;

		if (whitelabeling) {
			data.value.whitelabeling = whitelabeling;
		}

		if (newLogotypeFile.value) {
			data.value = await resource.putLogotype(data.value.id as number, newLogotypeFile.value);
			newLogotypeFile.value = null;
		}

		subscriptionResource = useCustomerSubscription(data.value.id as number);

		if (props.forCreate) {
			keepSaving = await saveUserForCreate();
		}

		if (keepSaving) {
			keepSaving = await saveSubscriptions();
		}

		if (keepSaving) {
			keepSaving = await saveLawFirms();
		}

		if (keepSaving) {
			keepSaving = await saveLanguages();
		}

		if (keepSaving) {
			emit('saved', data.value);
		}

	} catch (e) {
		handleFormError(e as Error, errors);
	}
};

const saveUserForCreate = async (): Promise<boolean> => {
	if (user.value.id) {
		return true;
	}

	userErrors.value = {};

	try {
		const userData = Object.assign({}, user.value);
		userData.language = store.getters.userLanguage;

		user.value = await useCustomerUser(data.value?.id as number).post(userData);

		return true;
	} catch (e) {
		handleFormError(e as Error, userErrors);

		return false;
	}
};

const saveSubscriptions = async (): Promise<boolean> => {
	subscriptionErrors.value = {};

	const requests: Promise<boolean>[] = [];

	subscriptions.value.forEach((subscription: Partial<CustomerSubscription>, index: number) => {
		const subscriptionData: Record<string, unknown> = Object.assign({}, subscription);
		subscriptionData.product = subscription.product?.id;

		let request;

		if (subscription.id) {
			request = subscriptionResource.put(subscription.id, subscriptionData);
		} else {
			request = subscriptionResource.post(subscriptionData);
		}

		requests.push(request);

		request
			.then(subscription => {
				subscriptions.value.splice(index, 1, subscription);

				return true;
			})
			.catch(e => {
				handleFormError(e as Error, subscriptionErrors);

				return false;
			})
		;
	});

	const results = await Promise.all(requests);

	return results.reduce(
		(carry: boolean, result: boolean) => (!result ? false : carry),
		true,
	);
};

const saveLawFirms = async (): Promise<boolean> => {
	if (!data.value || !data.value?.law_firms) {
		throw new Error('No customer or law firm data');
	}

	lawFirmErrors.value = {};

	const requests: Promise<boolean>[] = [];

	data.value.law_firms.forEach((customerLawFirm: CustomerLawFirm) => {
		const request = resource.setLawFirm(data.value?.id as number, customerLawFirm);

		requests.push(request
			.then(() => {
				return true;
			})
			.catch(e => {
				handleFormError(e as Error, lawFirmErrors);

				return false;
			}),
		);
	});

	lawFirmsToRemove.forEach((lawForm: LawFirm, index: number) => {
		const request = resource.removeLawFirm(data.value?.id as number, lawForm.id);

		requests.push(request
			.then(() => {
				lawFirmsToRemove.splice(index, 1);

				return true;
			})
			.catch(e => {
				handleFormError(e as Error, lawFirmErrors);

				return false;
			}),
		);
	});

	const results = await Promise.all(requests);

	return results.reduce(
		(carry: boolean, result: boolean) => (!result ? false : carry),
		true,
	);
};

const saveLanguages = async (): Promise<boolean> => {
	if (!data.value || !data.value?.languages) {
		throw new Error('No customer or language data');
	}

	languageErrors.value = {};

	const requests: Promise<boolean>[] = [];

	languagesToAdd.forEach((languageId: string, index: number) => {
		const request = resource.setLanguage(data.value?.id as number, languageId);

		requests.push(request
			.then(() => {
				languagesToAdd.splice(index, 1);

				return true;
			})
			.catch(e => {
				handleFormError(e as Error, languageErrors);

				return false;
			}),
		);
	});

	languagesToRemove.forEach((languageId: string, index: number) => {
		const request = resource.removeLanguage(data.value?.id as number, languageId);

		requests.push(request
			.then(() => {
				languagesToRemove.splice(index, 1);

				return true;
			})
			.catch(e => {
				handleFormError(e as Error, languageErrors);

				return false;
			}),
		);
	});

	const results = await Promise.all(requests);

	storedLanguages = data.value.languages || [];

	return results.reduce(
		(carry: boolean, result: boolean) => (!result ? false : carry),
		true,
	);
};

return (_ctx: any,_cache: any) => {
  const _component_translate = _resolveComponent("translate")!
  const _directive_translate = _resolveDirective("translate")!

  return (_openBlock(), _createBlock(PageLayout, {
    "page-title": _ctx.pageTitle,
    "base-tag": "form",
    onSubmit: _withModifiers(save, ["prevent"])
  }, _createSlots({
    "header-actions": _withCtx(() => [
      _createVNode(AppButton, {
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('cancel')))
      }, {
        default: _withCtx(() => [
          _createVNode(_component_translate, null, {
            default: _withCtx(() => [
              _createTextVNode("Avbryt")
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(AppButton, { type: "submit" }, {
        default: _withCtx(() => [
          _createVNode(_component_translate, null, {
            default: _withCtx(() => [
              _createTextVNode("Spara")
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 2
  }, [
    (data.value)
      ? {
          name: "content",
          fn: _withCtx(() => [
            _createVNode(AppTabs, {
              tabs: tabs.value,
              current: currentTab.value,
              onOpen: _cache[1] || (_cache[1] = ($event: any) => (currentTab.value = $event))
            }, null, 8, ["tabs", "current"]),
            _withDirectives(_createElementVNode("div", _hoisted_1, [
              _createElementVNode("div", _hoisted_2, [
                _createVNode(CustomerFormBasics, {
                  customer: data.value,
                  "onUpdate:customer": _cache[2] || (_cache[2] = ($event: any) => ((data).value = $event)),
                  errors: errors.value,
                  onLawFirmRemoved: lawFirmRemoved
                }, null, 8, ["customer", "errors"]),
                (_ctx.forCreate)
                  ? (_openBlock(), _createBlock(CustomerFormUser, {
                      key: 0,
                      user: user.value,
                      "onUpdate:user": _cache[3] || (_cache[3] = ($event: any) => ((user).value = $event)),
                      errors: userErrors.value
                    }, null, 8, ["user", "errors"]))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", null, [
                (data.value.url)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                      _withDirectives((_openBlock(), _createElementBlock("h2", null, [
                        _createTextVNode("Logotyp & formulär")
                      ])), [
                        [_directive_translate]
                      ]),
                      (_ctx.customer.logotype_path)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_4, [
                            _createElementVNode("img", {
                              src: `/${_ctx.customer.logotype_path}`,
                              alt: "",
                              class: "whitelabel-logo-preview"
                            }, null, 8, _hoisted_5)
                          ]))
                        : _createCommentVNode("", true),
                      _createVNode(AppFormGroup, {
                        id: "customer_logotype",
                        label: _unref($gettext)('Logotyp')
                      }, {
                        default: _withCtx(() => [
                          _createVNode(AppFileInput, {
                            id: "customer_logotype",
                            errors: errors.value.logotype_path,
                            accept: "image/*",
                            onFile: logotypeSelected
                          }, null, 8, ["errors"])
                        ]),
                        _: 1
                      }, 8, ["label"]),
                      _createVNode(CustomerTicketFormInfo, { customer: data.value }, null, 8, ["customer"])
                    ]))
                  : _createCommentVNode("", true),
                _createVNode(CustomerFormLanguages, {
                  languages: _ctx.customer.languages,
                  subscriptions: subscriptions.value,
                  errors: languageErrors.value,
                  onLanguageAdded: languageAdded,
                  onLanguageRemoved: languageRemoved
                }, null, 8, ["languages", "subscriptions", "errors"])
              ])
            ], 512), [
              [_vShow, currentTab.value === 'basics']
            ]),
            _withDirectives(_createElementVNode("div", _hoisted_6, [
              _withDirectives((_openBlock(), _createElementBlock("h2", null, [
                _createTextVNode("Abonnemang & tillval")
              ])), [
                [_directive_translate]
              ]),
              (_ctx.forCreate && subscriptions.value.length > 0)
                ? (_openBlock(), _createBlock(CustomerFormFirstSubscription, {
                    key: 0,
                    subscription: subscriptions.value[0],
                    "onUpdate:subscription": _cache[4] || (_cache[4] = ($event: any) => ((subscriptions.value[0]) = $event)),
                    errors: subscriptionErrors.value
                  }, null, 8, ["subscription", "errors"]))
                : (_openBlock(), _createBlock(CustomerFormSubscriptionList, {
                    key: 1,
                    subscriptions: subscriptions.value,
                    "onUpdate:subscriptions": _cache[5] || (_cache[5] = ($event: any) => ((subscriptions).value = $event))
                  }, null, 8, ["subscriptions"])),
              (data.value.invoice)
                ? (_openBlock(), _createBlock(CustomerFormInvoiceAddress, {
                    key: 2,
                    invoiceAddress: data.value.invoice,
                    "onUpdate:invoiceAddress": _cache[6] || (_cache[6] = ($event: any) => ((data.value.invoice) = $event)),
                    errors: errors.value.invoice || {}
                  }, null, 8, ["invoiceAddress", "errors"]))
                : _createCommentVNode("", true)
            ], 512), [
              [_vShow, currentTab.value === 'subscriptions']
            ])
          ]),
          key: "0"
        }
      : undefined
  ]), 1032, ["page-title", "onSubmit"]))
}
}

})